import { Link } from 'react-router-dom';
import { useSiteSettings } from '@/settings/useSiteSettings';
import { isExternalHref, type SiteLink } from '@/services/siteSettings';

function FooterLink({ link }: { link: SiteLink }) {
  if (isExternalHref(link.href)) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="text-sm text-surface-400 hover:text-white transition-colors">
        {link.label}
      </a>
    );
  }

  return (
    <Link to={link.href} className="text-sm text-surface-400 hover:text-white transition-colors">
      {link.label}
    </Link>
  );
}

function LinkColumn({ title, links }: { title: string; links: SiteLink[] }) {
  const enabledLinks = links.filter((link) => link.enabled);
  if (enabledLinks.length === 0) return null;

  return (
    <div>
      <h4 className="text-sm font-semibold text-white mb-4">{title}</h4>
      <ul className="space-y-2.5">
        {enabledLinks.map((link) => (
          <li key={link.id}>
            <FooterLink link={link} />
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Footer() {
  const { settings } = useSiteSettings();
  const footer = settings.footer;
  const logoUrl = footer.logoUrl || settings.general.logoUrl;
  const activeSocials = footer.socialLinks.filter((social) => social.enabled);

  return (
    <footer className="bg-surface-900 text-surface-300">
      <div className="px-4 md:px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10">
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-2.5 mb-4">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={footer.brandName}
                  className="w-8 h-8 rounded object-contain"
                />
              ) : (
                <span className="w-8 h-8 rounded bg-brand-600 text-white text-sm font-bold flex items-center justify-center">
                  {footer.brandName.charAt(0).toUpperCase()}
                </span>
              )}
              <span className="font-semibold text-white text-base">{footer.brandName}</span>
            </div>
            <p className="text-sm text-surface-400 leading-relaxed max-w-xs">
              {footer.description}
            </p>
          </div>

          <LinkColumn title="Products" links={footer.productLinks} />
          <LinkColumn title="Support" links={footer.supportLinks} />
          <LinkColumn title="Company" links={footer.companyLinks} />
        </div>

        <div className="border-t border-surface-800 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-xs text-surface-500">
            &copy; {new Date().getFullYear()} {footer.copyrightText}
          </p>
          <div className="flex items-center gap-4">
            {activeSocials.map((social) => (
              <a
                key={social.id}
                href={social.href}
                target={isExternalHref(social.href) ? '_blank' : undefined}
                rel={isExternalHref(social.href) ? 'noopener noreferrer' : undefined}
                className="text-surface-500 hover:text-white transition-colors"
                aria-label={social.label}
              >
                <i className={`${social.icon} text-lg`} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
